import { useHelper } from "@hooks/useHelper";
import { useImage } from "@hooks/useImage";
import { useRichText } from "@hooks/useRichText";
import { useRoutes } from "@hooks/useRoutes";
import {
  type ComponentProps,
  type FromSanityGraphQL,
  type GatsbyCustomImage,
  type ResolvedLink,
} from "@ts/components";
import { type Content, type SectionFeaturedImageContent } from "@ts/sanity";
import React, { useCallback, type FC } from "react";

export type FeaturedImageContentInputProps = ComponentProps &
  FromSanityGraphQL<SectionFeaturedImageContent>;

export type FeaturedImageContentOutputProps = FeaturedImageContentInputProps & {
  logo: {
    src?: string;
    alt?: string;
  };
  sections: {
    reverse?: boolean;
    contents?: Content;
    image?: GatsbyCustomImage;
    imageLink?: ResolvedLink | null;
  }[];
  onClickImage: (imageLink: ResolvedLink) => void;
};

export const withFeaturedImageContent =
  (Component: FC<FeaturedImageContentOutputProps>) =>
  ({
    name = "FeaturedImageContent",
    analytics,
    sectionFirst,
    sectionSecond,
    logo,
    ...props
  }: FeaturedImageContentInputProps) => {
    const { parseContent } = useRichText();
    const { getFluidImage } = useImage();
    const { linkResolver } = useRoutes();
    const { handleClickOnCustomContentLink } = useHelper();

    const sections = [
      {
        reverse: sectionFirst?.reverse,
        contents: sectionFirst?.contents?.map((content) =>
          parseContent(content?.content),
        ),
        image: getFluidImage(sectionFirst?.image, { layout: "fullWidth" }),
        imageLink:
          sectionFirst?.image?.imagelink &&
          sectionFirst?.image?.imagelink.length
            ? linkResolver(sectionFirst.image.imagelink[0])
            : null,
      },
      {
        reverse: sectionSecond?.reverse,
        contents: sectionSecond?.contents?.map((content) =>
          parseContent(content?.content),
        ),
        image: getFluidImage(sectionSecond?.image, { layout: "fullWidth" }),
        imageLink:
          sectionSecond?.image?.imagelink &&
          sectionSecond?.image?.imagelink.length
            ? linkResolver(sectionSecond.image.imagelink[0])
            : null,
      },
    ];
    const logoImage = {
      src: logo?.image?.asset?.url,
      alt: logo?.image?.alt,
    };

    const handleClickImage = useCallback(
      (imageLink: ResolvedLink) => {
        if (!analytics) {
          return;
        }

        handleClickOnCustomContentLink(imageLink);
      },
      [analytics, handleClickOnCustomContentLink],
    );

    Component.displayName = name;
    return (
      <Component
        {...props}
        logo={logoImage}
        sections={sections}
        onClickImage={handleClickImage}
      />
    );
  };
